import { Component } from '@theme/component';

import { onDocumentLoaded, changeMetaThemeColor, setHeaderMenuStyle } from '@theme/utilities';

import {
  getScrollTop,
  getScrollEventTarget,
  getIntersectionRoot,
  scrollContainerMediaQuery,
} from '@theme/scroll-container';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer
 * @property {HTMLElement} headerMenu
 * @property {HTMLElement} headerRowTop
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  /** @type {number | null} */
  #menuDrawerHiddenWidth = null;

  /** @type {IntersectionObserver | null} */
  #intersectionObserver = null;

  /** @type {EventTarget | null} */
  #scrollContainer = null;

  /** @type {boolean} */
  #offscreen = false;

  /** @type {number} */
  #lastScrollTop = 0;

  /** @type {number | null} */
  #scrollRafId = null;

  /** @type {number | null} */
  #lastHeaderHeight = null;

  #resizeObserver = new ResizeObserver(([entry]) => {
    const borderBox = entry?.borderBoxSize?.[0];
    if (!borderBox) return;

    const roundedHeaderHeight = Math.round(borderBox.blockSize);

    if (roundedHeaderHeight !== this.#lastHeaderHeight) {
      this.#lastHeaderHeight = roundedHeaderHeight;
      document.body.style.setProperty('--header-height', `${roundedHeaderHeight}px`);
    }

    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
  });

  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
      root: getIntersectionRoot(),
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      if (alwaysSticky) {
        const nextState = entry.isIntersecting ? 'inactive' : 'active';

        if (this.dataset.stickyState !== nextState) {
          this.dataset.stickyState = nextState;
        }

        if (this.dataset.themeColor) {
          changeMetaThemeColor(this.dataset.themeColor);
        }

        return;
      }

      this.#offscreen = !entry.isIntersecting || this.dataset.stickyState === 'active';
    }, config);

    this.#intersectionObserver.observe(this);
  };

  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.#menuDrawerHiddenWidth = window.innerWidth;
    } else {
      this.#menuDrawerHiddenWidth = null;

      const overflowList = this.querySelector('overflow-list');
      overflowList?.removeAttribute('minimum-reached');
    }

    setHeaderMenuStyle();
  }

  #handleBreakpointChange = () => {
    const stickyMode = this.getAttribute('sticky');
    if (!stickyMode) return;

    if (this.#scrollContainer) {
      this.#scrollContainer.removeEventListener('scroll', this.#handleWindowScroll);
      this.#scrollContainer = null;
    }

    if (stickyMode === 'scroll-up') {
      this.#scrollContainer = getScrollEventTarget();

      this.#scrollContainer.addEventListener(
        'scroll',
        this.#handleWindowScroll,
        { passive: true }
      );
    }

    this.#intersectionObserver?.disconnect();
    this.#intersectionObserver = null;

    this.#observeStickyPosition(stickyMode === 'always');
  };

  #handleWindowScroll = () => {
    if (this.#scrollRafId !== null) return;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#updateScrollState();
    });
  };

  #updateScrollState = () => {
    const stickyMode = this.getAttribute('sticky');

    if (stickyMode !== 'scroll-up' || !this.#offscreen) return;

    const scrollTop = getScrollTop();
    const isScrollingUp = scrollTop < this.#lastScrollTop;
    const isAtTop = this.getBoundingClientRect().top >= 0;

    if (isScrollingUp) {
      if (isAtTop) {
        this.#offscreen = false;
        this.dataset.stickyState = 'inactive';
        this.dataset.scrollDirection = 'none';
      } else {
        this.dataset.stickyState = 'active';
        this.dataset.scrollDirection = 'up';
      }
    } else {
      this.dataset.scrollDirection = 'none';
      this.dataset.stickyState = 'idle';
    }

    this.#lastScrollTop = scrollTop;
  };

  connectedCallback() {
    super.connectedCallback();

    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);

    const stickyMode = this.getAttribute('sticky');

    if (stickyMode) {
      this.#observeStickyPosition(stickyMode === 'always');

      /*
       * PawVique использует sticky="always".
       * Поэтому постоянный scroll listener здесь не нужен.
       * Он подключается только для режима scroll-up.
       */
      if (stickyMode === 'scroll-up') {
        this.#scrollContainer = getScrollEventTarget();

        this.#scrollContainer.addEventListener(
          'scroll',
          this.#handleWindowScroll,
          { passive: true }
        );
      }

      scrollContainerMediaQuery.addEventListener(
        'change',
        this.#handleBreakpointChange
      );
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();

    this.removeEventListener(
      'overflowMinimum',
      this.#handleOverflowMinimum
    );

    scrollContainerMediaQuery.removeEventListener(
      'change',
      this.#handleBreakpointChange
    );

    this.#scrollContainer?.removeEventListener(
      'scroll',
      this.#handleWindowScroll
    );

    this.#scrollContainer = null;

    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(this.#scrollRafId);
      this.#scrollRafId = null;
    }

    this.#lastHeaderHeight = null;

    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector('header-component');
  const headerGroup = document.querySelector('#header-group');

  if (!headerGroup) return;

  let lastHeaderGroupHeight = null;

  const resizeObserver = new ResizeObserver((entries) => {
    const headerGroupHeight = entries.reduce((totalHeight, entry) => {
      if (
        entry.target !== header ||
        (
          header instanceof HTMLElement &&
          header.hasAttribute('transparent') &&
          header.parentElement?.nextElementSibling
        )
      ) {
        return totalHeight + (entry.borderBoxSize?.[0]?.blockSize ?? 0);
      }

      return totalHeight;
    }, 0);

    const roundedHeaderGroupHeight = Math.round(headerGroupHeight);

    if (roundedHeaderGroupHeight === lastHeaderGroupHeight) return;

    lastHeaderGroupHeight = roundedHeaderGroupHeight;

    document.body.style.setProperty(
      '--header-group-height',
      `${roundedHeaderGroupHeight}px`
    );
  });

  const observeChildren = () => {
    for (const element of headerGroup.children) {
      if (element instanceof HTMLElement) {
        resizeObserver.observe(element);
      }
    }
  };

  observeChildren();

  const mutationObserver = new MutationObserver((mutations) => {
    if (!mutations.some((mutation) => mutation.type === 'childList')) return;

    observeChildren();
  });

  mutationObserver.observe(headerGroup, {
    childList: true,
  });
});